import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { getFaq, createFaq, updateFaq, deleteFaq } from 'actions/admin/faqs';
import { handleResponse } from 'actions/actionHelpers';
import { convertToNumber } from 'helpers/StringHelper';
import routes from 'routes';
import faqCategories from 'enums/faqCategories';
import { createTiptapEditor } from 'form/tiptapUtil';
import { mustBeNullOrNumber } from 'form/validation';

import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import AdminFormButtons from '../../presentational/AdminFormButtons';
import AdminFormFields from '../../presentational/AdminFormFields';
import AdminFormWrapper from '../../presentational/AdminFormWrapper';
import AdminPage from '../../presentational/AdminPage';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Loader from 'common/Loader';
import MarginBox from 'common/MarginBox';
import { Link } from 'react-router-dom';
import Form from 'form/Form';
import { TextField, Checkboxes, showErrorOnBlur } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import TiptapEditor from 'form/TiptapEditor';

const Faq = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();

    const [faq, setFaq] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialValues, setInitialValues] = useState(undefined);
    const { faqId } = params;

    const questionEditor = createTiptapEditor();
    const answerEditor = createTiptapEditor();

    useEffect(() => {
        if(convertToNumber(faqId)) {
            loadFaq();
        } else {
            setFaq({
                category: faqCategories.booking.key
            });
            setIsLoading(false);
        }
    }, [faqId]);

    useEffect(() => {
        if(faq) {
            setInitialValues({
                category: faq.category,
                order: faq.order,
                primary: faq.primary
            });
            questionEditor.commands.setContent(faq.question);
            answerEditor.commands.setContent(faq.answer);
        }
    }, [faq]);

    const loadFaq = () => {
        setIsLoading(true);
        dispatch(getFaq(faqId))
            .then(handleResponse(
                response => {
                    setFaq(response.payload);
                    setIsLoading(false);
                },
                () => setIsLoading(false)
            ));
    };

    const handleFormSubmit = formValues => {
        setIsSubmitting(true);
        const request = {
            category: formValues.category,
            question: questionEditor.getHTML(),
            answer: answerEditor.getHTML(),
            order: convertToNumber(formValues.order),
            primary: formValues.primary
        };
        const action = convertToNumber(faqId)
            ? updateFaq(faqId, request)
            : createFaq(request);
        dispatch(action)
            .then(handleResponse(
                () => {
                    history.push(routes.admin.faqs);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'FAQ was successfully saved.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const handleDelete = () => {
        if(!convertToNumber(faqId)) {
            return;
        }
        setIsSubmitting(true);
        dispatch(deleteFaq(faqId))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.faqs);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'FAQ was successfully deleted.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const title = 'FAQ: ' + (faq ? (`Item ${faq.id}` ?? 'New FAQ') : 'Loading...');

    return (
        <AdminPage title={title}>
            {
                isLoading && <Loader />
            }
            {
                !isLoading && faq && (
                    <AdminContentWrapper type="detail">
                        <AdminFormWrapper isSubmitting={isSubmitting}>
                            <Form
                                initialValues={initialValues}
                                onSubmit={handleFormSubmit}
                            >
                                {({ handleSubmit, invalid }) => (
                                    <form onSubmit={handleSubmit}>
                                        <AdminFormFields>
                                            <TextField
                                                select
                                                name="category"
                                                label="Category"
                                                variant="outlined"
                                            >
                                                {
                                                    Object.values(faqCategories).map(item =>
                                                        <MenuItem key={item.key} value={item.key}>{item.title}</MenuItem>
                                                    )
                                                }
                                            </TextField>

                                            <MarginBox top={2}>
                                                <Typography variant="body1" gutterBottom>Question</Typography>
                                                <TiptapEditor editor={questionEditor} />
                                            </MarginBox>

                                            <MarginBox top={2} bottom={2}>
                                                <Typography variant="body1" gutterBottom>Answer</Typography>
                                                <TiptapEditor editor={answerEditor} />
                                            </MarginBox>

                                            <TextField
                                                name="order"
                                                label="Sort order"
                                                variant="outlined"
                                                showError={showErrorOnBlur}
                                                fieldProps={{ validate: mustBeNullOrNumber }}
                                            />

                                            <Checkboxes
                                                name="primary"
                                                data={{
                                                    label: 'Show on start page',
                                                    value: true
                                                }}
                                            />

                                        </AdminFormFields>
                                        <AdminFormButtons>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={invalid}
                                            >
                                                Save
                                            </Button>
                                            {
                                                convertToNumber(faqId) &&
                                                (
                                                    <Button
                                                        type="button"
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={handleDelete}
                                                    >
                                                        Delete
                                                    </Button>
                                                )
                                            }
                                            <Button
                                                component={Link}
                                                to={routes.admin.faqs}
                                                variant="outlined"
                                                color="primary"
                                            >
                                                Back
                                            </Button>
                                        </AdminFormButtons>
                                    </form>
                                )}
                            </Form>
                        </AdminFormWrapper>
                    </AdminContentWrapper>
                )
            }
        </AdminPage>
    );
};

export default Faq;
